import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  makeStyles,
  createStyles,
  Theme,
  Avatar,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: '8px',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      padding: '16px',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    avatar: {
      width: '48px',
      height: '48px',
      backgroundColor: '#003F2D',
      color: '#FFFFFF',
      fontWeight: 600,
      fontSize: '16px',
      marginRight: '16px',
    },
    userInfo: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    },
    userName: {
      fontSize: '16px',
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    emailRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '14px',
      color: theme.palette.text.secondary,
    },
    verifiedIcon: {
      width: '16px',
      height: '16px',
      color: '#00A86B',
    },
    metadata: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      fontSize: '14px',
      color: theme.palette.text.secondary,
    },
    expandIcon: {
      transition: 'transform 0.3s',
    },
    expandIconRotated: {
      transform: 'rotate(180deg)',
    },
    content: {
      borderTop: `1px solid ${theme.palette.divider}`,
      padding: '16px',
    },
    promptList: {
      paddingLeft: '20px',
      margin: 0,
      '& li': {
        fontSize: '14px',
        color: theme.palette.text.primary,
        lineHeight: '20px',
        marginBottom: '8px',
        '&:last-child': {
          marginBottom: 0,
        },
      },
    },
  }),
);

export interface UserPromptCardProps {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  initials: string;
  date: string;
  product: string;
  prompts: string[];
}

export const UserPromptCard: React.FC<UserPromptCardProps> = ({
  name,
  email,
  initials,
  date,
  product,
  prompts,
}) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  return (
    <Box className={classes.card}>
      <Box className={classes.header} onClick={handleToggle}>
        <Avatar className={classes.avatar}>{initials}</Avatar>
        <Box className={classes.userInfo}>
          <Typography className={classes.userName}>{name}</Typography>
          <Box className={classes.emailRow}>
            <span>{email}</span>
            <VerifiedUserIcon className={classes.verifiedIcon} />
          </Box>
          <Box className={classes.metadata}>
            <span>{date}</span>
            <span>Product: {product}</span>
          </Box>
        </Box>
        <IconButton
          className={`${classes.expandIcon} ${expanded ? classes.expandIconRotated : ''}`}
          aria-label="expand"
          size="small"
        >
          <ExpandMoreIcon />
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box className={classes.content}>
          <ul className={classes.promptList}>
            {(prompts ?? []).map((prompt, index) => (
              <li key={index}>{prompt || 'No prompt text'}</li>
            ))}
          </ul>
        </Box>
      </Collapse>
    </Box>
  );
};
